import React, {useEffect, useCallback, useMemo, useState, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import PropTypes from 'prop-types';
import {PRICE_COMPARISON_PATH} from '../../constants/routePaths';
import {fetchUploadedInvoiceDetail} from '../../actions/invoiceUpload.action';
import {updateBasket} from '../../actions/basket.action';
import {
  DetailWrapper,
  BackLink,
  PageTitle,
  PageIntro,
  SectionTitle,
  ComparisonHeader,
  ComparisonTitle,
  ComparisonSubtitle,
  OverviewCard,
  OverviewItem,
  SavingsHero,
  SavingsHeroLabel,
  SavingsHeroValue,
  SavingsHeroCaption,
  AddAllButton,
  SecondaryAddLink,
  SuccessMessage,
  LineItemCard,
  LineItemHeader,
  LineItemDescription,
  LineItemDetails,
  LineItemMatch,
  MatchInfo,
  ProductLink,
  SavingBadge,
  NoMatchLabel,
  InferredNote,
  ProductSwapNote,
  ProductSwapLabel,
  StatusBadge,
  ErrorMessage,
  ProcessingMessage,
  ProgressSteps,
  ProgressStep,
  ProgressDot,
  ProgressConnector,
  ProgressCaption,
  ProgressBar,
  ProgressBarFill,
  MatchingPlaceholder,
  PromptCard,
  PromptCardHeader,
  PromptCardTitle,
  PromptCardText,
  PromptCardButton,
  PromptCardDivider,
  PromptCardFooter,
} from './InvoiceUploadPage.style';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_SOURCES,
  INVOICE_UPLOAD_STATUS_LABELS,
  INVOICE_UPLOAD_MATCHING_STATUSES,
  INVOICE_UPLOAD_COMPLETE_STATUSES,
  INVOICE_UPLOAD_TERMINAL_STATUSES,
  INVOICE_UPLOAD_POLL_INTERVAL_MS,
  INVOICE_UPLOAD_DETAIL_POLL_START_DELAY_MS,
  INVOICE_UPLOAD_MATCH_FALLBACK_MS,
  INVOICE_UPLOAD_PROGRESS_STEP_LABELS,
  INVOICE_UPLOAD_READING_MESSAGES,
  INVOICE_UPLOAD_READING_MESSAGE_INTERVAL_MS,
  INVOICE_UPLOAD_MATCHING_CAPTION,
  INVOICE_UPLOAD_TIMED_OUT_CAPTION,
  INVOICE_UPLOAD_ANONYMOUS_TIMED_OUT_CAPTION,
  INVOICE_UPLOAD_STATUS_MATCHED,
  INVOICE_UPLOAD_DEFAULT_COMPARISON_WINDOW_DAYS,
} from '../../constants/constants';
import {
  LOGIN_PATH,
  INVOICE_REGISTRATION_PATH,
} from '../../constants/routePaths';
import {formatDate, formatCurrency} from '../../helpers/invoiceUpload.helper';

const StepProgress = ({current}) => (
  <ProgressSteps>
    {INVOICE_UPLOAD_PROGRESS_STEP_LABELS.map((label, i) => {
      const state = i < current ? 'done' : i === current ? 'active' : 'pending';
      return (
        <React.Fragment key={label}>
          {i > 0 && <ProgressConnector $filled={i <= current} />}
          <ProgressStep $state={state}>
            <ProgressDot $state={state}>
              {state === 'done' ? '✓' : i + 1}
            </ProgressDot>
            {label}
          </ProgressStep>
        </React.Fragment>
      );
    })}
  </ProgressSteps>
);

StepProgress.propTypes = {
  current: PropTypes.number.isRequired,
};

// A line is addable to the basket when it has a confident, priced JJ match.
const isAddableItem = item =>
  item.matchedProductId &&
  !item.nonProductLine &&
  !item.noMatchAvailable &&
  !item.insufficientDetail &&
  !item.measurementInferred;

const InvoiceUploadDetail = ({match, history}) => {
  const dispatch = useDispatch();
  const id = match.params.id;
  const uploadState = useSelector(state => state.invoiceUpload || {});
  const auth = useSelector(state => state.auth);
  const detail = uploadState.detail || null;
  const detailLoading = uploadState.detailLoading || false;
  const detailError = uploadState.detailError || null;

  // Frozen at mount rather than read live: anonymous invoices are never
  // linked to an account, so this id must keep being fetched from the public
  // endpoint even if the visitor logs in while watching this page — deciding
  // from live auth on every poll would 404 the moment they did. This is only
  // for picking which endpoint to poll — anything about what to *show* the
  // visitor (e.g. can they add items to their basket right now) must use the
  // live `auth` below instead, or it'll keep showing anonymous-only UI after
  // they log in without a full page reload.
  const [isAnonymousInvoice] = useState(() => !auth);
  const isLoggedIn = !!auth;

  const [addedCount, setAddedCount] = useState(0);
  const [matchTimedOut, setMatchTimedOut] = useState(false);
  const [readingMsgIndex, setReadingMsgIndex] = useState(0);

  const status = detail && detail.overview && detail.overview.status;
  const savingsData = detail && detail.savingsData;
  const isExtracting = status === 'PROCESSING';
  const isMatching = INVOICE_UPLOAD_MATCHING_STATUSES.indexOf(status) !== -1;
  const isComplete = INVOICE_UPLOAD_COMPLETE_STATUSES.indexOf(status) !== -1;
  const isFailed = status === 'FAILED';
  // The invoice-scanning service marks a re-upload of an already-known invoice
  // as DUPLICATE (detected after extraction). It's a terminal state with no
  // comparison of its own — show a message and stop the progress/polling UI.
  const isDuplicate = status === 'DUPLICATE';
  // Prices move, so an invoice from outside the service's comparison window is
  // rejected after extraction rather than matched — comparing today's catalogue
  // against months-old prices would invent a saving. Terminal, like DUPLICATE.
  const isTooOld = status === 'TOO_OLD';
  // Nothing invoice-like in the upload: the service deletes the pages and the
  // extracted data, so there is no overview to show and nothing to retry for —
  // only a message telling the uploader what went in.
  const isNotAnInvoice = status === 'NOT_AN_INVOICE';
  // Anything the comparison can end on, listed once in constants.js.
  const isTerminal = INVOICE_UPLOAD_TERMINAL_STATUSES.indexOf(status) !== -1;
  const comparisonWindowDays =
    (detail && detail.overview && detail.overview.comparisonWindowDays) ||
    INVOICE_UPLOAD_DEFAULT_COMPARISON_WINDOW_DAYS;
  // Matching has produced results once the savings data carries matched lines.
  // A processed invoice can sit in SUBMITTED / READY_FOR_REVIEW with a full,
  // computed savings breakdown (e.g. when JJ isn't cheaper overall, so it never
  // auto-advances to MATCHED) — those results are ready to show even though the
  // status still reads as in-progress.
  const hasResults = !!(savingsData && savingsData.matchedLineItems > 0);
  const resultsReady = isComplete || hasResults;
  // Keep polling only while a non-terminal stage has nothing to show yet; stop
  // the moment results land so we don't re-fetch the same payload forever.
  const isPolling = (isExtracting || isMatching) && !resultsReady;
  const currentStep = resultsReady ? 2 : isMatching ? 1 : 0;
  const inProgress = !resultsReady && !isTerminal;
  // In the results view, present the status as "Results ready" even when the
  // backend still reports an in-progress status (SUBMITTED / READY_FOR_REVIEW)
  // for a fully computed comparison, so the badge doesn't contradict the page.
  const displayStatus =
    resultsReady && !isComplete ? INVOICE_UPLOAD_STATUS_MATCHED : status;

  const progressCaption = matchTimedOut
    ? !isLoggedIn
      ? INVOICE_UPLOAD_ANONYMOUS_TIMED_OUT_CAPTION
      : INVOICE_UPLOAD_TIMED_OUT_CAPTION
    : currentStep >= 1
    ? INVOICE_UPLOAD_MATCHING_CAPTION
    : INVOICE_UPLOAD_READING_MESSAGES[readingMsgIndex];

  const pollRef = useRef(null);
  const fallbackRef = useRef(null);

  useEffect(() => {
    // Anonymous invoices poll the public endpoint regardless of auth (see
    // isAnonymousInvoice above); the authenticated flow still needs a token.
    if ((isAnonymousInvoice || auth) && id) {
      // If we already hold this invoice (e.g. arriving from the upload page's
      // status poll), refresh silently so the results render immediately rather
      // than flashing the full-page loader.
      const haveThisInvoice =
        detail && detail.overview && detail.overview.id === id;
      dispatch(
        fetchUploadedInvoiceDetail(id, haveThisInvoice, isAnonymousInvoice)
      );
      setAddedCount(0);
      setMatchTimedOut(false);
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_VIEW_DETAIL,
        {category: 'Compare Prices', invoiceId: id}
      );
    }
  }, [isAnonymousInvoice, auth, id]);

  // Silently poll while extraction/matching is still in progress. The boolean
  // dependency only flips at a stage transition, so the interval is set up once
  // on entering the polling phase and torn down once on reaching a terminal one.
  useEffect(() => {
    if ((!isAnonymousInvoice && !auth) || !id || !isPolling) {
      return undefined;
    }
    // The mount effect above already fetches the invoice once, so give
    // processing a head start before the first re-poll rather than hammering
    // the endpoint every INVOICE_UPLOAD_POLL_INTERVAL_MS from the outset.
    const startPollTimeout = setTimeout(() => {
      pollRef.current = setInterval(() => {
        dispatch(fetchUploadedInvoiceDetail(id, true, isAnonymousInvoice));
      }, INVOICE_UPLOAD_POLL_INTERVAL_MS);
    }, INVOICE_UPLOAD_DETAIL_POLL_START_DELAY_MS);
    fallbackRef.current = setTimeout(() => {
      setMatchTimedOut(true);
    }, INVOICE_UPLOAD_MATCH_FALLBACK_MS);
    return () => {
      clearTimeout(startPollTimeout);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (fallbackRef.current) {
        clearTimeout(fallbackRef.current);
        fallbackRef.current = null;
      }
    };
  }, [isAnonymousInvoice, auth, id, isPolling]);

  // Advance the reading sub-message while on the (long) reading step, so the
  // copy visibly changes and the wait doesn't look frozen. Holds on the last
  // message rather than looping.
  useEffect(() => {
    if (currentStep !== 0 || isTerminal || matchTimedOut) {
      setReadingMsgIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setReadingMsgIndex(i =>
        Math.min(i + 1, INVOICE_UPLOAD_READING_MESSAGES.length - 1)
      );
    }, INVOICE_UPLOAD_READING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [currentStep, isTerminal, matchTimedOut]);

  const goBack = () => history.push('/price-comparison');

  const addableItems = useMemo(() => {
    const savings = detail && detail.savingsData;
    if (!savings || !Array.isArray(savings.lineItems)) return [];
    return savings.lineItems.filter(isAddableItem);
  }, [detail]);

  // Total savings is the sum of the lines where JJ costs less (or the same
  // price) only — lines where JJ costs more don't erode the figure. If nothing
  // is cheaper there is no saving to show.
  const totalPositiveSavings = useMemo(() => {
    const savings = detail && detail.savingsData;
    if (!savings || !Array.isArray(savings.lineItems)) return 0;
    return savings.lineItems.reduce(
      (sum, item) =>
        item.saving != null && item.saving > 0 ? sum + item.saving : sum,
      0
    );
  }, [detail]);

  // The subset of addable lines where JJ actually comes out cheaper — the
  // lower-commitment "add only the savings" action.
  const cheaperItems = useMemo(
    () => addableItems.filter(item => item.saving != null && item.saving > 0),
    [addableItems]
  );

  // AddAllButton/SecondaryAddLink both disable via `addedCount > 0`, but that's a
  // state read that only updates next render — a rapid double click/tap on either
  // (or one of each in quick succession) can both land before that re-render and
  // double up every basket line. This ref blocks it immediately.
  const addingToBasketRef = useRef(false);

  const addItemsToBasket = useCallback(
    (items, source) => {
      if (!items.length || addingToBasketRef.current) return;
      addingToBasketRef.current = true;
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_ADD_ALL_TO_BASKET,
        {
          category: 'Compare Prices',
          invoiceId: id,
          itemCount: items.length,
        }
      );
      items.forEach(item => {
        dispatch(
          updateBasket({
            itemId: item.matchedProductId,
            quantity: 1,
            step: 1,
            collectionPrice: item.matchedProductPrice,
            deliveryPrice: item.matchedProductPrice,
          })
        );
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.ADD_PRODUCT, {
          product: {
            itemId: item.matchedProductId,
            itemName: item.matchedProductName,
          },
          quantity: 1,
          source,
        });
      });
      setAddedCount(items.length);
    },
    [dispatch, id]
  );

  // First load (e.g. straight after upload): we don't have the status yet, so
  // show the progress tracker on its first step rather than a bare spinner.
  if (detailLoading && !detail) {
    return (
      <DetailWrapper>
        <BackLink onClick={goBack}>
          &lsaquo; Back to JJ Price Compare AI
        </BackLink>
        <PageTitle>Price Comparison</PageTitle>
        <StepProgress current={0} />
        <ProgressCaption>{progressCaption}</ProgressCaption>
        <ProgressBar>
          <ProgressBarFill />
        </ProgressBar>
      </DetailWrapper>
    );
  }

  if (detailError) {
    return (
      <DetailWrapper>
        <BackLink onClick={goBack}>
          &lsaquo; Back to JJ Price Compare AI
        </BackLink>
        <ErrorMessage>{detailError}</ErrorMessage>
      </DetailWrapper>
    );
  }

  if (!detail) {
    return (
      <DetailWrapper>
        <BackLink onClick={goBack}>
          &lsaquo; Back to JJ Price Compare AI
        </BackLink>
        <ErrorMessage>Invoice not found.</ErrorMessage>
      </DetailWrapper>
    );
  }

  const {overview} = detail;
  const extractedLineItems =
    savingsData && Array.isArray(savingsData.lineItems)
      ? savingsData.lineItems
      : [];

  return (
    <DetailWrapper>
      <BackLink onClick={goBack}>&lsaquo; Back to JJ Price Compare AI</BackLink>
      <ComparisonHeader>
        <ComparisonTitle>Your price comparison</ComparisonTitle>
        <ComparisonSubtitle>
          Based on the invoice you supplied &mdash; like-for-like against JJ
          Foodservice.
        </ComparisonSubtitle>
      </ComparisonHeader>

      {inProgress && (
        <>
          <StepProgress current={currentStep} />
          <ProgressCaption>{progressCaption}</ProgressCaption>
          {!matchTimedOut && (
            <ProgressBar>
              <ProgressBarFill />
            </ProgressBar>
          )}
        </>
      )}

      {/* One persistent card for the whole lifecycle — this used to be a separate
          OverviewCard pre-results and DetailsCard post-results, which meant the status
          row was unmounted and a differently-laid-out one mounted in its place the
          instant results arrived. Keeping a single card and just adding/updating fields
          in place is what stops that show/hide flash. */}
      <OverviewCard>
        <OverviewItem>
          <label>Status</label>
          <StatusBadge $status={displayStatus}>
            {INVOICE_UPLOAD_STATUS_LABELS[displayStatus] || displayStatus}
          </StatusBadge>
        </OverviewItem>
        <OverviewItem>
          <label>Supplier</label>
          <span>{overview.supplierName || '-'}</span>
        </OverviewItem>
        <OverviewItem>
          <label>Invoice No.</label>
          <span>{overview.invoiceNumber || '-'}</span>
        </OverviewItem>
        {resultsReady && savingsData && savingsData.matchedLineItems > 0 && (
          <OverviewItem>
            <label>Items matched</label>
            <span>
              {savingsData.matchedLineItems} of {savingsData.lineItems.length}
            </span>
          </OverviewItem>
        )}
        <OverviewItem>
          <label>Invoice Date</label>
          <span>{formatDate(overview.invoiceDate)}</span>
        </OverviewItem>
        <OverviewItem>
          <label>Uploaded</label>
          <span>{formatDate(overview.uploadedAt)}</span>
        </OverviewItem>
      </OverviewCard>

      {isFailed && (
        <ErrorMessage>
          We couldn&apos;t process this invoice. Please try uploading it again
          or contact customer support if the problem persists.
        </ErrorMessage>
      )}

      {isDuplicate && (
        <ProcessingMessage>
          <h3>This invoice has already been uploaded</h3>
          <p>
            We recognised this invoice from a previous upload, so we
            haven&apos;t created a duplicate comparison.{' '}
            {!isLoggedIn ? (
              'Please use the link or bookmark from your original comparison to view it.'
            ) : (
              <>
                Your original comparison is available from the{' '}
                <Link to={PRICE_COMPARISON_PATH}>Price Comparison</Link> list.
              </>
            )}
          </p>
        </ProcessingMessage>
      )}

      {isTooOld && (
        <ProcessingMessage>
          <h3>This invoice is too old to compare</h3>
          <p>
            {detail && detail.overview && detail.overview.invoiceDate ? (
              <>
                This invoice is dated {formatDate(detail.overview.invoiceDate)}.{' '}
              </>
            ) : null}
            Prices change constantly, so we only compare invoices from the last{' '}
            {comparisonWindowDays} days — anything older wouldn&apos;t give you
            a figure you could rely on. Upload a recent invoice to see how JJ
            compares.
          </p>
        </ProcessingMessage>
      )}

      {isNotAnInvoice && (
        <ProcessingMessage>
          <h3>We couldn&apos;t find an invoice in that upload</h3>
          <p>
            Nothing we could read as a supplier invoice came back from that
            file, so there&apos;s nothing to compare. Try again with a clear,
            full photo or PDF of the invoice itself.
          </p>
        </ProcessingMessage>
      )}

      {isComplete && !savingsData && (
        <ProcessingMessage>
          <h3>Savings data unavailable</h3>
          <p>
            This invoice has been processed but the detailed savings breakdown
            is not available. Please contact customer support if you need
            assistance.
          </p>
        </ProcessingMessage>
      )}

      {isComplete && savingsData && savingsData.matchedLineItems === 0 && (
        <ProcessingMessage>
          <h3>We couldn&apos;t compare any items on this invoice</h3>
          <p>
            We weren&apos;t able to confidently match your invoice lines to our
            catalogue.
            {overview.invoiceTotal != null && (
              <>
                {' '}
                Your invoice total was {formatCurrency(overview.invoiceTotal)}.
              </>
            )}
          </p>
          <p>
            If you&apos;d like a member of the team to take a look, please
            contact customer support.
          </p>
        </ProcessingMessage>
      )}

      {resultsReady && savingsData && savingsData.matchedLineItems > 0 && (
        <>
          {totalPositiveSavings > 0 ? (
            <SavingsHero>
              <SavingsHeroLabel>You save with JJ</SavingsHeroLabel>
              <SavingsHeroValue>
                {formatCurrency(totalPositiveSavings)}
              </SavingsHeroValue>
              <SavingsHeroCaption>
                Based on the items where JJ costs less
              </SavingsHeroCaption>
            </SavingsHero>
          ) : (
            <SavingsHero>
              <SavingsHeroCaption>
                We compared {savingsData.matchedLineItems}{' '}
                {savingsData.matchedLineItems === 1 ? 'item' : 'items'} on your
                invoice. On this order, JJ didn&apos;t cost less overall &mdash;
                but prices change, so it&apos;s worth checking again next time.
              </SavingsHeroCaption>
            </SavingsHero>
          )}

          {addableItems.length > 0 && !isLoggedIn && (
            <PromptCard>
              <PromptCardHeader>
                <div>
                  <PromptCardTitle>
                    {isAnonymousInvoice
                      ? 'Ready to start saving?'
                      : totalPositiveSavings > 0
                      ? `Lock in your ${formatCurrency(
                          totalPositiveSavings
                        )} saving`
                      : 'Lock in your saving'}
                  </PromptCardTitle>
                  <PromptCardText>
                    Open a free JJ Foodservice account and start saving today!
                  </PromptCardText>
                </div>
                <PromptCardButton
                  as={Link}
                  to={INVOICE_REGISTRATION_PATH.replace(':invoiceId', id)}
                >
                  Create a free account
                </PromptCardButton>
              </PromptCardHeader>
              <PromptCardDivider />
              <PromptCardFooter>
                Already have an account with us?{' '}
                <Link to={LOGIN_PATH}>Log in</Link>
              </PromptCardFooter>
            </PromptCard>
          )}

          {addableItems.length > 0 && isLoggedIn && (
            <>
              <AddAllButton
                onClick={() =>
                  addItemsToBasket(
                    addableItems,
                    ANALYTICS_SOURCES.PRICE_COMPARE_ADD_ALL
                  )
                }
                disabled={addedCount > 0}
              >
                {addedCount > 0
                  ? `${addedCount} ${
                      addedCount === 1 ? 'item' : 'items'
                    } added to basket`
                  : `Add all ${addableItems.length} ${
                      addableItems.length === 1 ? 'item' : 'items'
                    } to basket`}
              </AddAllButton>
              {addedCount === 0 &&
                cheaperItems.length > 0 &&
                cheaperItems.length < addableItems.length && (
                  <SecondaryAddLink
                    onClick={() =>
                      addItemsToBasket(
                        cheaperItems,
                        ANALYTICS_SOURCES.PRICE_COMPARE_ADD_CHEAPER
                      )
                    }
                  >
                    Or add only the {cheaperItems.length}{' '}
                    {cheaperItems.length === 1 ? 'item' : 'items'} where JJ
                    costs less
                  </SecondaryAddLink>
                )}
              {addedCount > 0 && (
                <SuccessMessage>
                  {addedCount} {addedCount === 1 ? 'item has' : 'items have'}{' '}
                  been added to your basket.{' '}
                  <ProductLink
                    href="/basket"
                    onClick={e => {
                      e.preventDefault();
                      history.push('/basket');
                    }}
                  >
                    View basket
                  </ProductLink>
                </SuccessMessage>
              )}
            </>
          )}
        </>
      )}

      {extractedLineItems.length > 0 && (
        <>
          <SectionTitle>
            {savingsData.matchedLineItems === 0
              ? 'Items on your invoice'
              : 'Item-by-item comparison'}
          </SectionTitle>
          {savingsData.matchedLineItems > 0 && (
            <PageIntro>
              <p>
                Each of your lines, matched to the equivalent JJ product on a
                like-for-like basis. Tap any JJ item for full details and
                availability.
              </p>
            </PageIntro>
          )}

          {extractedLineItems.map((item, idx) => {
            return (
              <LineItemCard
                key={idx}
                $noMatch={item.noMatchAvailable}
                $productSwap={item.isProductSwap}
              >
                <LineItemHeader>
                  <div>
                    <LineItemDescription>
                      {item.description}
                    </LineItemDescription>
                    <LineItemDetails>
                      Qty: {item.invoiceQuantity} &times;{' '}
                      {formatCurrency(item.invoiceUnitPrice)} ={' '}
                      {formatCurrency(item.invoiceLineTotal)}
                    </LineItemDetails>
                  </div>
                </LineItemHeader>

                {item.nonProductLine ? (
                  <InferredNote>
                    Discount / credit line &ndash; nothing to match.
                  </InferredNote>
                ) : item.insufficientDetail ? (
                  <NoMatchLabel>
                    Unable to match product. Additional details (size, weight or
                    volume) required.
                  </NoMatchLabel>
                ) : item.noMatchAvailable ? (
                  <NoMatchLabel>
                    No JJ alternative available. Please contact us to discuss.
                  </NoMatchLabel>
                ) : isMatching &&
                  !item.matchedProductId &&
                  !item.matchedProductName ? (
                  // Matching resolves line by line — a poll can land savingsData with some
                  // lines already matched and this one not yet, with none of the flags above
                  // set either. Without this branch it fell through to the match display below
                  // with nothing to show, then flipped to the real match a poll or two later —
                  // the "rows refreshing" effect this guards against.
                  <MatchingPlaceholder>
                    Finding you the best JJ price&hellip;
                  </MatchingPlaceholder>
                ) : (
                  <>
                    <LineItemMatch>
                      <MatchInfo>
                        {item.isProductSwap && (
                          <ProductSwapLabel>JJ swap</ProductSwapLabel>
                        )}
                        <strong>
                          {item.matchedProductId ? (
                            <ProductLink
                              href={`/productDetail/${item.matchedProductId}`}
                              onClick={e => {
                                e.preventDefault();
                                GoogleAnalyticsHelper.trackEvent(
                                  ANALYTICS_EVENTS.COMPARE_PRICES_PRODUCT_CLICK,
                                  {
                                    category: 'Compare Prices',
                                    productId: item.matchedProductId,
                                    productName: item.matchedProductName,
                                    saving: item.saving,
                                  }
                                );
                                history.push(
                                  `/productDetail/${item.matchedProductId}`
                                );
                              }}
                            >
                              {item.matchedProductId} &ndash;{' '}
                              {item.matchedProductName}
                            </ProductLink>
                          ) : (
                            item.matchedProductName
                          )}
                        </strong>
                        JJ unit price {formatCurrency(item.matchedProductPrice)}
                        {item.effectiveTotal != null && (
                          <>
                            {' '}
                            | JJ Equivalent Total{' '}
                            {formatCurrency(item.effectiveTotal)}
                          </>
                        )}
                        {item.measurementInferred && (
                          <InferredNote>
                            We can&apos;t calculate the saving on this line
                            &ndash; your invoice didn&apos;t show a clear weight
                            or quantity.
                          </InferredNote>
                        )}
                      </MatchInfo>
                      {item.saving != null && (
                        <SavingBadge $positive={item.saving >= 0}>
                          {item.saving === 0
                            ? 'Same price'
                            : item.saving > 0
                            ? `Save ${formatCurrency(item.saving)}`
                            : `${formatCurrency(Math.abs(item.saving))} more`}
                        </SavingBadge>
                      )}
                    </LineItemMatch>
                    {item.isProductSwap && item.swappedFromProductId && (
                      <ProductSwapNote>
                        Matched brand product:{' '}
                        <ProductLink
                          href={`/productDetail/${item.swappedFromProductId}`}
                          onClick={e => {
                            e.preventDefault();
                            history.push(
                              `/productDetail/${item.swappedFromProductId}`
                            );
                          }}
                        >
                          {item.swappedFromProductId} &ndash;{' '}
                          {item.swappedFromProductName}
                        </ProductLink>
                        {item.swappedFromProductPrice != null && (
                          <>
                            {' '}
                            &middot; JJ price:{' '}
                            {formatCurrency(item.swappedFromProductPrice)}
                          </>
                        )}
                      </ProductSwapNote>
                    )}
                  </>
                )}
              </LineItemCard>
            );
          })}
        </>
      )}
    </DetailWrapper>
  );
};

InvoiceUploadDetail.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default InvoiceUploadDetail;
